import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  Clock3,
  Eye,
} from 'lucide-react';

const STATUS_META = {
  pending: {
    label: 'Pendiente',
    className: 'bg-amber-100 text-amber-700',
  },
  approved: {
    label: 'Aprobada',
    className: 'bg-emerald-100 text-emerald-700',
  },
  rejected: {
    label: 'Rechazada',
    className: 'bg-rose-100 text-rose-700',
  },
};

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const statusMeta = (status) =>
  STATUS_META[status] || {
    label: status || 'Desconocido',
    className: 'bg-slate-100 text-slate-600',
  };

export default function ModuleExamRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [examStudies, setExamStudies] = useState([]);
  const [loadingExamStudies, setLoadingExamStudies] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState('');

  const load = async () => {
    setLoading(true);

    try {
      const { data } = await api.get('/admin/module-exam-requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Error cargando solicitudes de examen'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();

    return requests.filter((request) => {
      if (statusFilter !== 'all' && request.status !== statusFilter) {
        return false;
      }

      if (!search) return true;

      const haystack = [
        request.student_name,
        request.student_email,
        request.teacher_name,
        request.teacher_email,
        request.teacher_comment,
        `módulo ${request.module_slot}`,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [requests, q, statusFilter]);

  const counts = useMemo(
    () => ({
      all: requests.length,
      pending: requests.filter((r) => r.status === 'pending').length,
      approved: requests.filter((r) => r.status === 'approved').length,
      rejected: requests.filter((r) => r.status === 'rejected').length,
    }),
    [requests]
  );

  const openReview = async (request) => {
    setSelectedRequest(request);
    setSelectedStudyId('');
    setExamStudies([]);
    setReviewOpen(true);

    if (request.status !== 'pending') return;

    setLoadingExamStudies(true);

    try {
      const { data } = await api.get(
        `/admin/module-exam-studies/${request.module_slot}`
      );

      setExamStudies(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          'No se pudieron cargar los estudios del módulo'
      );
    } finally {
      setLoadingExamStudies(false);
    }
  };

  const closeReview = () => {
    if (reviewing) return;

    setReviewOpen(false);
    setSelectedRequest(null);
    setConfirmOpen(false);
    setConfirmStatus(null);
    setExamStudies([]);
    setSelectedStudyId('');
  };

  const requestReview = (status) => {
    if (!selectedRequest || reviewing) return;

    setConfirmStatus(status);
    setConfirmOpen(true);
  };

  const reviewRequest = async (status) => {
    if (!selectedRequest || reviewing) return;

    setReviewing(true);

    try {
      const payload = { status };

      if (status === 'approved') {
        if (!selectedStudyId) {
          toast.error('Selecciona el estudio que se utilizará como examen');
          setReviewing(false);
          return;
        }

        payload.study_id = selectedStudyId;
      }

      await api.patch(
        `/admin/module-exam-requests/${selectedRequest.id}`,
        payload
      );

      toast.success(
        status === 'approved'
          ? 'Solicitud aprobada'
          : 'Solicitud rechazada'
      );

      setReviewOpen(false);
      setSelectedRequest(null);
      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'No se pudo revisar la solicitud'
      );
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-blue-600" />
            Solicitudes de examen
          </span>
        }
        subtitle="Revisa las solicitudes de examen de módulo enviadas por los profesores."
        testId="module-exam-requests-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            Todas
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1">
            {counts.all}
          </div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">
            Pendientes
          </div>
          <div className="text-2xl font-bold text-amber-800 mt-1">
            {counts.pending}
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">
            Aprobadas
          </div>
          <div className="text-2xl font-bold text-emerald-800 mt-1">
            {counts.approved}
          </div>
        </div>

        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-xs uppercase tracking-widest text-rose-700 font-bold">
            Rechazadas
          </div>
          <div className="text-2xl font-bold text-rose-800 mt-1">
            {counts.rejected}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="module-exam-search"
            placeholder="Buscar por alumno, profesor o comentario…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-full"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            data-testid="module-exam-status-filter"
            className="rounded-full md:w-52"
          >
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos ({counts.all})</SelectItem>
            <SelectItem value="pending">
              Pendientes ({counts.pending})
            </SelectItem>
            <SelectItem value="approved">
              Aprobadas ({counts.approved})
            </SelectItem>
            <SelectItem value="rejected">
              Rechazadas ({counts.rejected})
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Alumno</th>
                <th className="text-left p-3">Módulo</th>
                <th className="text-left p-3">Profesor</th>
                <th className="text-left p-3">Comentario</th>
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Acción</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="p-10 text-center text-slate-500"
                  >
                    Cargando solicitudes…
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="p-10 text-center text-slate-500"
                    data-testid="module-exam-empty"
                  >
                    No hay solicitudes que coincidan con los filtros.
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((request) => {
                  const meta = statusMeta(request.status);
                  const pending = request.status === 'pending';

                  return (
                    <tr
                      key={request.id}
                      className="border-t border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="p-3">
                        <div className="font-semibold text-slate-800">
                          {request.student_name || 'Alumno'}
                        </div>
                        {request.student_email && (
                          <div className="text-xs text-slate-500">
                            {request.student_email}
                          </div>
                        )}
                      </td>

                      <td className="p-3">
                        <span className="font-semibold text-slate-700">
                          Módulo {request.module_slot}
                        </span>
                      </td>

                      <td className="p-3">
                        <div className="font-medium text-slate-700">
                          {request.teacher_name || 'Profesor'}
                        </div>
                        {request.teacher_email && (
                          <div className="text-xs text-slate-500">
                            {request.teacher_email}
                          </div>
                        )}
                      </td>

                      <td className="p-3 max-w-xs">
                        <div className="truncate text-slate-600">
                          {request.teacher_comment || 'Sin comentario'}
                        </div>
                      </td>

                      <td className="p-3 whitespace-nowrap text-slate-600">
                        {formatDate(request.requested_at || request.created_at)}
                      </td>

                      <td className="p-3">
                        <Badge className={meta.className}>
                          {request.status === 'pending' && (
                            <Clock3 className="w-3 h-3 mr-1" />
                          )}
                          {request.status === 'approved' && (
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                          )}
                          {request.status === 'rejected' && (
                            <XCircle className="w-3 h-3 mr-1" />
                          )}
                          {meta.label}
                        </Badge>
                      </td>

                      <td className="p-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => openReview(request)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {pending ? 'Revisar' : 'Ver'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Revisar solicitud de examen</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Alumno
                  </div>
                  <div className="font-semibold text-slate-800">
                    {selectedRequest.student_name || 'Alumno'}
                  </div>
                  {selectedRequest.student_email && (
                    <div className="text-xs text-slate-500">
                      {selectedRequest.student_email}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Módulo
                  </div>
                  <div className="font-semibold text-slate-800">
                    Módulo {selectedRequest.module_slot}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Profesor
                  </div>
                  <div className="font-semibold text-slate-800">
                    {selectedRequest.teacher_name || 'Profesor'}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Comentario del profesor
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">
                    {selectedRequest.teacher_comment || 'Sin comentario'}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Fecha de solicitud
                  </div>
                  <div className="text-sm text-slate-700">
                    {formatDate(
                      selectedRequest.requested_at ||
                        selectedRequest.created_at
                    )}
                  </div>
                </div>
              </div>

              {selectedRequest.status === 'pending' ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Selecciona una acción para resolver esta solicitud.
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  Esta solicitud ya fue revisada y no puede modificarse desde
                  aquí.
                </div>
              )}
            </div>
          )}

          {selectedRequest?.status === 'pending' && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-2">
                Study para el examen
              </div>

              {loadingExamStudies ? (
                <div className="text-sm text-slate-600">
                  Cargando Studies del módulo...
                </div>
              ) : examStudies.length === 0 ? (
                <div className="text-sm text-rose-600">
                  No hay Studies disponibles para este módulo.
                </div>
              ) : (
                <Select
                  value={selectedStudyId}
                  onValueChange={setSelectedStudyId}
                >
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="Selecciona el Study que se utilizará como examen" />
                  </SelectTrigger>

                  <SelectContent>
                    {examStudies.map((study) => (
                      <SelectItem key={study.id} value={study.id}>
                        {study.name} · {study.activities_count} actividades
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="text-xs text-slate-500 mt-2">
                El Study seleccionado se utilizará para crear el examen final
                del módulo.
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={closeReview}
              disabled={reviewing}
            >
              Cerrar
            </Button>

            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => requestReview('rejected')}
                  disabled={reviewing}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  {reviewing ? 'Guardando…' : 'Rechazar'}
                </Button>

                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => requestReview('approved')}
                  disabled={reviewing || !selectedStudyId}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {reviewing ? 'Guardando…' : 'Aprobar'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (reviewing) return;
          setConfirmOpen(open);
          if (!open) setConfirmStatus(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmStatus === 'approved'
                ? '¿Aprobar solicitud de examen?'
                : '¿Rechazar solicitud de examen?'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-2">
            <p className="text-sm text-slate-600">
              {confirmStatus === 'approved'
                ? `Estás autorizando la solicitud de examen de ${
                    selectedRequest?.student_name || 'este alumno'
                  } para ${selectedRequest?.module_slot
                    ? `el Módulo ${selectedRequest.module_slot}`
                    : 'este módulo'}.`
                : `La solicitud de ${
                    selectedRequest?.student_name || 'este alumno'
                  } quedará marcada como rechazada.`}
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
                setConfirmStatus(null);
              }}
              disabled={reviewing}
            >
              Cancelar
            </Button>

            <Button
              variant="outline"
              className={
                confirmStatus === 'approved'
                  ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                  : 'border-rose-200 text-rose-700 hover:bg-rose-50'
              }
              onClick={async () => {
                const status = confirmStatus;
                setConfirmOpen(false);
                setConfirmStatus(null);
                await reviewRequest(status);
              }}
              disabled={reviewing || !confirmStatus}
            >
              {confirmStatus === 'approved'
                ? 'Confirmar aprobación'
                : 'Confirmar rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
